/**
 * Active-call state, derived from the snapshot CallView stores on a
 * successful call. Shared by the tracker button (status dialog + FAB state)
 * and the map (call marker). Session/localStorage only - nothing server-side.
 */

/**
 * @returns {null | {
 *   deadline: number, langskomenLabel: string|null, aantal: string,
 *   straat: string, nr: string, postcode: string, gemeente: string,
 *   lat: number|null, lng: number|null
 * }} the active call, or null when there is none / it has expired.
 */
export function getActiveCall() {
    const lastCallTime = parseInt(localStorage.getItem('lastCallTime') || '0', 10);
    if (!lastCallTime) return null;

    const langskomen = localStorage.getItem('callLangskomen') || ''; // "HH:MM:SS"

    let deadline;
    const m = langskomen.match(/^(\d{1,2}):(\d{2})/);
    if (m) {
        // Anchor the "langskomen" time to the day the call was placed, so a
        // call placed at 23:50 for 23:59 still expires at midnight, not the
        // next day.
        const d = new Date(lastCallTime);
        d.setHours(parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
        deadline = d.getTime();
        // Guard: if the parsed time somehow lands before the call moment,
        // fall back to a one-hour window.
        if (deadline <= lastCallTime) deadline = lastCallTime + 60 * 60 * 1000;
    } else {
        deadline = lastCallTime + 60 * 60 * 1000; // legacy fallback: 1 hour
    }

    if (Date.now() >= deadline) return null; // term expired -> callable again

    const lat = parseFloat(localStorage.getItem('callLat'));
    const lng = parseFloat(localStorage.getItem('callLng'));

    return {
        deadline,
        langskomenLabel: m ? `${m[1].padStart(2, '0')}u${m[2]}` : null,
        aantal: localStorage.getItem('callAantal') || '',
        straat: localStorage.getItem('callStraat') || '',
        nr: localStorage.getItem('callNr') || '',
        postcode: localStorage.getItem('callPostcode') || '',
        gemeente: localStorage.getItem('callGemeente') || '',
        lat: isNaN(lat) ? null : lat,
        lng: isNaN(lng) ? null : lng,
    };
}

/**
 * Clear the locally-stored active call so getActiveCall() returns null again
 * (used when the user cancels their call). Only touches the call snapshot
 * keys; server-side removal is a separate remove_oproep request. Dropping
 * lastCallTime alone already makes getActiveCall() return null, but the rest
 * are cleared too so no stale snapshot lingers.
 */
export function clearActiveCall() {
    [
        'lastCallTime', 'callLangskomen', 'callAantal', 'callStraat',
        'callNr', 'callPostcode', 'callGemeente', 'callLat', 'callLng',
    ].forEach(key => localStorage.removeItem(key));
}
